'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Mail, ChevronRight } from 'lucide-react';
import { toast } from '@/components/ui/toast';
import { CreateProjectModal } from '@/components/modals';
import { authApiCall } from '@/lib/helpers/axios';

interface ServerActions {
  createProject: (
    projectName: string,
    projectDescription?: string,
  ) => Promise<{ success: boolean; projectId?: string; error?: string }>;
}

interface HomeBoxesProps {
  actions: ServerActions;
}

export default function HomeBoxes({ actions }: HomeBoxesProps) {
  const router = useRouter();
  const [meetingUrl, setMeetingUrl] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [isUrlValid, setIsUrlValid] = useState(true);
  const [botEmail, setBotEmail] = useState('');
  const [isSendingBot, setIsSendingBot] = useState(false);
  const [showCurrentMeetingInfo, setShowCurrentMeetingInfo] = useState(false);
  const [showFutureMeetingInfo, setShowFutureMeetingInfo] = useState(false);

  const isValidMeetingUrl = (url: string) => {
    if (!url.trim()) return true; // Empty URL is valid (neutral state)

    const zoomRegex = /^https:\/\/(.*\.)?zoom\.us\//;
    const meetRegex = /^https:\/\/meet\.google\.com\//;
    const teamsRegex = /^https:\/\/([a-zA-Z0-9-]+\.)?teams\.microsoft\.com\//;

    return zoomRegex.test(url) || meetRegex.test(url) || teamsRegex.test(url);
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setMeetingUrl(url);
    setIsUrlValid(isValidMeetingUrl(url));
  };

  const handleSendMeetingUrl = async () => {
    if (!isValidMeetingUrl(meetingUrl) || isSendingBot) return;

    setIsSendingBot(true);

    try {
      await authApiCall(`bot/start`, {
        method: 'POST',
        config: {
          data: { meetingUrl },
        },
      });

      toast.success(
        'Bot Sent Successfully',
        'Your meeting bot has been invited and will join the meeting shortly.',
      );
      setShowCurrentMeetingInfo(true);
    } catch (e: any) {
      console.error(e);
      if (e?.response?.data?.error === 'Meeting is in progress') {
        toast.error(
          'Meeting in progress — The meeting is already in session. Please try again later.',
        );
      } else {
        toast.error(
          'Failed to Send Bot',
          'Unable to invite the bot to your meeting. Please try again or check your meeting URL.',
        );
      }
    } finally {
      // Keep button disabled for 2 seconds to prevent double-clicking
      setTimeout(() => {
        setIsSendingBot(false);
      }, 2000);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isUrlValid && meetingUrl.trim() && !isSendingBot) {
      handleSendMeetingUrl();
    }
  };

  const handleCopyEmail = async () => {
    try {
      const emailAdResponse = await authApiCall(`bot/email`).then(
        (response) => response?.data,
      );

      const emailAddress = emailAdResponse?.emailAddress;
      setBotEmail(emailAddress);

      await navigator.clipboard.writeText(emailAddress);
      toast.success(
        'Email Copied',
        "The bot's email address has been copied to your clipboard.",
      );
      setShowFutureMeetingInfo(true);
    } catch (error) {
      toast.error(
        'Copy Failed',
        'Email address is shown. Please select and copy manually.',
      );
    }
  };

  const handleCreateProject = async () => {
    console.log('Creating project:', {
      name: projectName,
      description: projectDescription,
    });
    const result = await actions.createProject(projectName, projectDescription);

    if (result.success) {
      toast.success('Project Created', 'Project created successfully.');
      setIsDialogOpen(false);
      setProjectName('');
      setProjectDescription('');

      // Redirect to projects page to show the new project
      router.push('/projects');
      router.refresh();
    } else {
      toast.error(
        'Creation Failed',
        result.error || 'Failed to create project.',
      );
    }
  };

  const handleCancelProject = () => {
    setIsDialogOpen(false);
    setProjectName('');
    setProjectDescription('');
  };

  return (
    <div className={'flex flex-row gap-4 my-2'}>
      {/* Invite Bot to Current Meeting */}
      <div className="flex-1 rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Invite Bot to Current Meeting
        </h3>
        <p className="text-sm text-gray-600 mb-2">
          ...on Zoom, Teams or Google Meet
        </p>
        {showCurrentMeetingInfo && (
          <p className="text-xs text-blue-600 bg-blue-50 rounded px-2 py-1 mb-3">
            Your Bot will join shortly!
          </p>
        )}
        <div className="relative">
          <Input
            type="text"
            value={meetingUrl}
            onChange={handleUrlChange}
            onKeyDown={handleKeyDown}
            placeholder="Paste meeting URL"
            className={`pr-10 ${!isUrlValid ? '!border-red-500 focus-visible:!ring-white' : ''}`}
            aria-invalid={!isUrlValid}
          />
          <Button
            size="sm"
            variant="ghost"
            className="absolute right-1 top-1/2 -translate-y-1/2 w-7 p-0 hover:bg-transparent"
            disabled={!meetingUrl.trim() || !isUrlValid || isSendingBot}
            onClick={handleSendMeetingUrl}
          >
            {isSendingBot ? (
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send
                className={`w-4 h-4 ${!meetingUrl.trim() || !isUrlValid || isSendingBot ? 'text-gray-400' : 'text-blue-500'}`}
              />
            )}
          </Button>
        </div>
        {!isUrlValid && meetingUrl.trim() && (
          <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
            Invalid meeting link. Please check and try again.
          </p>
        )}
      </div>

      {/* Invite Bot to a Future Meeting */}
      <div className="flex-1 rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Invite Bot to a Future Meeting
        </h3>
        {showFutureMeetingInfo && (
          <p className="text-xs text-blue-600 bg-blue-50 rounded px-2 py-1 mb-3">
            Add or share — bot will join your meeting.
          </p>
        )}

        {botEmail ? (
          <div>
            <p className="text-sm font-mono text-gray-800 select-all break-all mb-1">
              {botEmail}
            </p>
          </div>
        ) : (
          /**DEV NOTE: Please keep it here :)) */
          <p className="text-sm text-transparent select-none">
            ...on Zoom, Teams or Google Meet
          </p>
        )}
        <Button
          variant="outline"
          className="w-full justify-start gap-2 h-10 border-slate-200"
          onClick={handleCopyEmail}
        >
          <Mail className="w-4 h-4 text-blue-500" />
          Copy Email for Invite
        </Button>
      </div>

      {/* Create a New Project */}
      <div
        className="group flex-1 rounded-lg border border-slate-200 bg-white p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsDialogOpen(true)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Create a New Project
            </h3>
            <p className="text-sm text-gray-600">
              Start a new project, assign meetings and documents and generate
              key outputs.
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
        </div>
      </div>

      <CreateProjectModal
        isOpen={isDialogOpen}
        onClose={handleCancelProject}
        projectName={projectName}
        onProjectNameChange={setProjectName}
        projectDescription={projectDescription}
        onProjectDescriptionChange={setProjectDescription}
        onCreateProject={handleCreateProject}
      />
    </div>
  );
}
